import Mark from 'mark.js';
import {Scroll} from '@gongxigames/framework/core';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

const MAX_INPUT_SIZE = 50;
const MARGIN_TOP = 500;

export class HtmlSearch {
    private mark?: Mark;

    private root!: HTMLDivElement;
    private searchNavButtons!: HTMLDivElement;
    private numOfFoundItems!: HTMLParagraphElement;
    private nextButton!: HTMLButtonElement;
    private clearSearch!: HTMLButtonElement;
    private prevButton!: HTMLButtonElement;
    private searchInput!: HTMLInputElement;
    private query?: string;
    private prevQuery?: string;
    private customScroll?: Scroll;
    private searchableContainer?: HTMLElement;
    private currentSearchItems: HTMLElement[] = [];
    private currentSearchItemsIndex = 0;

    constructor(
        private documentQueryString: string,
        private currentClass = 'current',
        private searchTranslation: string,
    ) {
        this.createDomeComponent();
        this.addKeyboardListener();
        this.searchSet()
    }

    private addKeyboardListener() {
        strictUiEmitter.on('CORE.input.keyup', (e) => {
            if(e.key === 'Enter' && this.currentSearchItems.length) {
                this.selectItem(this.currentSearchItemsIndex + 1);
            }
        });
    }

    private onInputChange(): void {
        this.updateResultList();
        this.selectItem(0);
        this.updateSearchControls();
    }

    searchSet(searchStr?: string): void {
        const mark = this.getMark();

        this.query = searchStr;
        mark.unmark();

        if (this.query) {
            mark.mark(this.query, {
                separateWordSearch: false,
                done: this.onInputChange.bind(this),
            });
        } else {
            this.onInputChange();
        }
    }

    get controls(): HTMLDivElement {
        return this.root;
    }

    set scroll(value: Scroll) {
        this.customScroll = value;
    }

    private updateSearchControls(): void {
        const {currentSearchItems} = this;
        const {length} = currentSearchItems;
        const shouldShowControls = length > 0 && this.query;

        this.searchNavButtons.style.display = this.query ? 'flex' : 'none';
        this.clearSearch.style.display = this.query ? 'flex' : 'none';

        this.numOfFoundItems!.textContent = length ? `${this.currentSearchItemsIndex + 1}/${length}` : '0/0';

        if (shouldShowControls) {
            this.searchNavButtons.classList.remove('disabled');
        } else {
            this.searchNavButtons.classList.add('disabled');
        }
    }

    private selectItem(index: number) {
        const {currentSearchItems, currentClass} = this;
        const {length} = currentSearchItems;

        if (!length) {
            this.currentSearchItemsIndex = 0;

            return;
        }

        this.currentSearchItemsIndex = (index % length + length) % length;

        this.updateSearchControls();

        currentSearchItems.forEach((item, i) => {
            const isSelected = i === this.currentSearchItemsIndex;

            if (isSelected) {
                item.classList.add(currentClass);

                const scrollToPosition = item.getBoundingClientRect().top - MARGIN_TOP;

                if (this.customScroll) {
                    this.customScroll.scrollTo(scrollToPosition);
                } else {
                    item.scrollIntoView({behavior: 'smooth'});
                }

            } else {
                item.classList.remove(currentClass);
            }
        });
    }

    public clear(): void {
        this.searchInput.value = '';
        this.prevQuery = undefined;
        this.searchSet();
    }

    private updateResultList(): void {
        const results = this.searchableContainer?.querySelectorAll('mark');

        this.currentSearchItemsIndex = 0;
        this.currentSearchItems.length = 0;

        results?.forEach(item => this.currentSearchItems.push(item));
    }

    private getMark(): Mark {
        if (!this.mark) {
            this.ensureDomeElement();
            this.mark = new Mark(this.searchableContainer!);
        }

        return this.mark;
    }

    private ensureDomeElement(): void {
        this.searchableContainer ??= document.querySelector(this.documentQueryString)!;
    }

    private createDomeComponent() {
        this.root = document.createElement('div');

        this.root.classList.add('search-root');
        this.root.innerHTML = `
            <input class='search-input' data-testid="search-input" type='text' placeholder=${this.searchTranslation}>
            <div class="controls">
                <button class="clear-search" data-testid="search-clear">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <mask id="mask0_504_39" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                            <rect width="24" height="24" fill="#D9D9D9"/>
                        </mask>
                        <g mask="url(#mask0_504_39)">
                            <path d="M8.4 17L12 13.4L15.6 17L17 15.6L13.4 12L17 8.4L15.6 7L12 10.6L8.4 7L7 8.4L10.6 12L7 15.6L8.4 17ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z" fill="white"/>
                        </g>
                    </svg>
                </button>
                <div class="search-results-navigation">
                     <div class="separator"></div>
                    <p class="indicator" data-testid="search-indicator">0/0</p>
                    <div class='search-buttons'>
                        <button class="search-btn next" type="button" data-testid="search-btn-down">
                        <svg width="12" height="8" viewBox="0 0 12 8" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 2.8L1.4 7.4L0 6L6 0L12 6L10.6 7.4L6 2.8Z" fill="white"/>
                        </svg>
                        </button>
                        
                        <button class="search-btn prev" type="button" data-testid="search-btn-up">
                        <svg width="12" height="8" viewBox="0 0 12 8" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 7.4L0 1.4L1.4 0L6 4.6L10.6 0L12 1.4L6 7.4Z" fill="white"/>
                        </svg>
                        </button>
                    </div>
            </div>
            </div>
        `;

        this.searchNavButtons = this.root.querySelector('.search-results-navigation')!;
        this.numOfFoundItems = this.root.querySelector('.indicator')!;
        this.clearSearch = this.root.querySelector('.clear-search')!;
        this.nextButton = this.root.querySelector('.next')!;
        this.prevButton = this.root.querySelector('.prev')!;
        this.searchInput = this.root.querySelector('.search-input')!;

        this.nextButton.addEventListener('click', () => this.selectItem(this.currentSearchItemsIndex - 1));
        this.prevButton.addEventListener('click', () => this.selectItem(this.currentSearchItemsIndex + 1));
        this.clearSearch.addEventListener('click', () => this.clear());

        [this.prevButton, this.nextButton].forEach(btn => btn.addEventListener('keyup', (e) => e.stopPropagation()));

        this.searchInput.addEventListener('input', event => {
            const value = (event.target as any)?.value ?? '';

            if ((value as string).length > MAX_INPUT_SIZE) {
                this.searchInput.value = value.substring(0, MAX_INPUT_SIZE);
            } else {
                this.searchSet(value);
            }
        });
    }

    public savePrevQuery(): void {
        this.prevQuery = this.searchInput.value;
    }

    public restorePrevQuery(): void {
        this.searchSet(this.prevQuery);
    }
}